import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { db, storage } from "../../../firebase/config";
import { collection, query, where, onSnapshot, addDoc, updateDoc, doc, orderBy, Timestamp, getDocs } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { useAuth } from "../../../context/AuthContext";
import { useTheme } from "../../../context/ThemeContext";
import Header from "../../../components/Header";
import { registrarAccion } from "../../../utils/trazabilidad";
import { exportarMovimientosCaja } from "../../../utils/exportar";

const CATEGORIAS_INGRESO = ['Consulta', 'Control', 'Estudio diagnóstico', 'Cirugía', 'Otros'];
const CATEGORIAS_EGRESO = ['Insumos clínicos', 'Honorarios', 'Servicios', 'Impuestos', 'Mantenimiento', 'Otros'];

export default function Caja() {
  const { currentUser, userRole, clinicaId, clinicaData } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [movimientos, setMovimientos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalNuevo, setModalNuevo] = useState(false);
  const [modalEditar, setModalEditar] = useState(null);
  const [datosEditar, setDatosEditar] = useState({});
  const [tipoModal, setTipoModal] = useState('ingreso');
  const [tab, setTab] = useState('resumen');
  const [periodo, setPeriodo] = useState('hoy');
  const [filtroFecha, setFiltroFecha] = useState(new Date().toISOString().split('T')[0]);
  const [archivoMovimiento, setArchivoMovimiento] = useState(null);
  const [nuevo, setNuevo] = useState({ tipo: 'ingreso', categoria: 'Consulta', detalle: '', monto: '', moneda: 'ARS', formaPago: 'Efectivo', paciente: '', pacienteId: '', medico: '', esDerivacion: false, medicoDerivador: '', cobertura: 'Particular', observaciones: '' });
  const [pacientesDB, setPacientesDB] = useState([]);
  const [sugerenciasPacientes, setSugerenciasPacientes] = useState([]);
  const [derivadoresDB, setDerivadoresDB] = useState([]);

  const esRecepcion = userRole === 'recepcion';
  const esAdmin = ['gestion_general', 'administracion'].includes(userRole);
  const canEdit = ['gestion_general', 'administracion'].includes(userRole);
  const canRegistrarIngreso = ['gestion_general', 'recepcion', 'administracion'].includes(userRole);
  const canRegistrarEgreso = ['gestion_general', 'administracion'].includes(userRole);
  const canVerTodo = ['gestion_general', 'administracion'].includes(userRole);

  const btnPrimaryStyle = { background: `linear-gradient(135deg, ${theme.accent}, ${theme.accentDark})`, border: 'none', borderRadius: '8px', padding: '8px 16px', fontSize: '13px', cursor: 'pointer', color: '#FFFFFF', fontWeight: '600', fontFamily: "'Montserrat', sans-serif" };
  const btnSecondaryStyle = { background: 'transparent', border: `0.5px solid ${theme.borderInput}`, borderRadius: '8px', padding: '8px 16px', fontSize: '13px', cursor: 'pointer', color: theme.secondary, fontFamily: "'Montserrat', sans-serif" };
  const inputStyle = { width: '100%', padding: '10px 14px', fontSize: '13px', border: `0.5px solid ${theme.borderInput}`, borderRadius: '8px', outline: 'none', color: theme.primary, background: theme.bgInput, boxSizing: 'border-box', fontFamily: "'Montserrat', sans-serif" };
  const labelStyle = { fontSize: '11px', color: theme.secondary, display: 'block', marginBottom: '6px', fontWeight: '500', letterSpacing: '1px' };
  const overlayStyle = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: theme.bgOverlay, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
  const modalStyle = { background: theme.bgModal, borderRadius: '16px', padding: '28px', width: '100%', maxWidth: '520px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.3)', border: `0.5px solid ${theme.border}` };

  function formatMonto(monto, moneda) {
    if (moneda === 'USD') return `USD ${parseFloat(monto).toFixed(2)}`;
    return `$${parseFloat(monto).toLocaleString('es-AR', { minimumFractionDigits: 2 })}`;
  }

  function totalPor(lista, moneda, formaPago) {
    return lista.filter(m => m.moneda === moneda && m.formaPago === formaPago).reduce((a, m) => a + m.monto, 0);
  }

  useEffect(() => {
    if (!clinicaId) return;
    let inicio, fin;
    const ahora = new Date();
    if (periodo === 'hoy') { inicio = new Date(filtroFecha + 'T00:00:00'); fin = new Date(filtroFecha + 'T23:59:59'); }
    else if (periodo === 'semana') { inicio = new Date(); inicio.setDate(ahora.getDate() - 7); inicio.setHours(0,0,0,0); fin = ahora; }
    else if (periodo === 'mes') { inicio = new Date(); inicio.setDate(1); inicio.setHours(0,0,0,0); fin = ahora; }
    else if (periodo === 'anio') { inicio = new Date(); inicio.setMonth(0); inicio.setDate(1); inicio.setHours(0,0,0,0); fin = ahora; }

    let q;
    if (esRecepcion) {
      q = query(collection(db, 'caja'), where('clinicaId', '==', clinicaId), where('registradoPor', '==', currentUser?.email), orderBy('fecha', 'desc'));
    } else {
      q = query(collection(db, 'caja'), where('clinicaId', '==', clinicaId), where('fecha', '>=', Timestamp.fromDate(inicio)), where('fecha', '<=', Timestamp.fromDate(fin)), orderBy('fecha', 'desc'));
    }

    const unsub = onSnapshot(q, snap => {
      setMovimientos(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return unsub;
  }, [filtroFecha, periodo, esRecepcion, currentUser, clinicaId]);
  
  useEffect(() => {
    if (!clinicaId) return;
    getDocs(query(collection(db, 'pacientes'), where('clinicaId', '==', clinicaId), orderBy('apellido')))
      .then(snap => setPacientesDB(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
    getDocs(query(collection(db, 'derivadores'), where('clinicaId', '==', clinicaId)))
      .then(snap => setDerivadoresDB(snap.docs.map(d => ({ id: d.id, ...d.data() }))))
      .catch(() => setDerivadoresDB([]));
  }, [clinicaId]);

  async function registrarMovimiento() {
    if (!nuevo.monto || parseFloat(nuevo.monto) <= 0) return;
    let archivoData = null;
    if (archivoMovimiento) {
      try {
        const nombreLimpio = archivoMovimiento.name.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9._-]/g, '');
        const storageRef = ref(storage, `caja/${clinicaId}/${Date.now()}_${nombreLimpio}`);
        await uploadBytes(storageRef, archivoMovimiento);
        const url = await getDownloadURL(storageRef);
        archivoData = { nombre: archivoMovimiento.name, url, tipo: archivoMovimiento.type };
      } catch (e) { console.warn('Error al subir archivo:', e); }
    }
    await addDoc(collection(db, 'caja'), {
      ...nuevo, monto: parseFloat(nuevo.monto),
      fecha: Timestamp.fromDate(new Date()),
      registradoPor: currentUser?.email, rol: userRole,
      estado: esRecepcion ? 'pendiente_arqueo' : 'confirmado',
      clinicaId,
      ...(archivoData ? { comprobante: archivoData } : {}),
    });
    await registrarAccion(currentUser?.email, userRole, 'Caja', nuevo.tipo === 'ingreso' ? 'Registrar ingreso' : 'Registrar egreso', `${nuevo.categoria} · ${nuevo.moneda} ${nuevo.monto}`, clinicaId);
    setModalNuevo(false);
    setArchivoMovimiento(null);
    setNuevo({ tipo: 'ingreso', categoria: 'Consulta', detalle: '', monto: '', moneda: 'ARS', formaPago: 'Efectivo', paciente: '', pacienteId: '', medico: '', esDerivacion: false, medicoDerivador: '', cobertura: 'Particular', observaciones: '' });
    setSugerenciasPacientes([]);
  }

  function abrirModal(tipo) { setTipoModal(tipo); setNuevo(prev => ({ ...prev, tipo, categoria: tipo === 'ingreso' ? 'Consulta' : 'Insumos clínicos' })); setModalNuevo(true); }

  const ingresos = movimientos.filter(m => m.tipo === 'ingreso' && (!esRecepcion || m.registradoPor === currentUser?.email));
  const egresos = movimientos.filter(m => m.tipo === 'egreso' && (!esRecepcion || m.registradoPor === currentUser?.email));

  return (
    <div style={{ minHeight: '100vh', background: theme.bg, fontFamily: "'Montserrat', sans-serif" }}>
      <Header titulo="Caja" backLabel="Dashboard" backRuta="/dashboard" />
      <div style={{ padding: '24px', background: theme.bg }}>

        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: '6px' }}>
            {['hoy','semana','mes','anio'].map(p => (
              <button key={p} onClick={() => setPeriodo(p)} style={{
                padding: '6px 12px', fontSize: '12px', borderRadius: '8px', cursor: 'pointer',
                border: periodo === p ? 'none' : `0.5px solid ${theme.borderInput}`,
                background: periodo === p ? theme.accent : 'transparent',
                color: periodo === p ? '#FFFFFF' : theme.secondary,
                fontFamily: "'Montserrat', sans-serif", fontWeight: periodo === p ? '600' : '400'
              }}>
                {p === 'hoy' ? 'Hoy' : p === 'semana' ? '7 días' : p === 'mes' ? 'Este mes' : 'Este año'}
              </button>
            ))}
          </div>
          {periodo === 'hoy' && (
            <input type="date" value={filtroFecha} onChange={e => setFiltroFecha(e.target.value)} style={{ ...inputStyle, width: 'auto' }} />
          )}
          {canVerTodo && (
            <div style={{ display: 'flex', gap: '6px', marginLeft: 'auto' }}>
              {['resumen', 'movimientos'].map(t => (
                <button key={t} onClick={() => setTab(t)} style={{
                  padding: '6px 12px', fontSize: '12px', borderRadius: '8px', cursor: 'pointer',
                  border: tab === t ? 'none' : `0.5px solid ${theme.borderInput}`,
                  background: tab === t ? theme.accentSecondary : 'transparent',
                  color: tab === t ? theme.primary : theme.secondary,
                  fontFamily: "'Montserrat', sans-serif"
                }}>
                  {t === 'resumen' ? 'Resumen' : 'Movimientos'}
                </button>
              ))}
            </div>
          )}
          <div style={{ display: 'flex', gap: '8px', marginLeft: canVerTodo ? '0' : 'auto' }}>
            {canVerTodo && (
              <button onClick={async () => { try { await exportarMovimientosCaja(movimientos, filtroFecha, clinicaData); alert('Exportado correctamente'); } catch(e) { alert('Error: ' + e.message); } }} style={btnSecondaryStyle}>
                Exportar a Excel
                </button>  
            )}
            {esRecepcion && <button onClick={() => navigate('/caja/arqueo')} style={btnSecondaryStyle}>Arqueo de turno</button>}
            {canRegistrarEgreso && <button onClick={() => abrirModal('egreso')} style={btnSecondaryStyle}>+ Egreso</button>}
            {canRegistrarIngreso && <button onClick={() => abrirModal('ingreso')} style={btnPrimaryStyle}>+ Ingreso</button>}
          </div>
        </div>

        {canVerTodo && tab === 'resumen' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px', marginBottom: '20px' }}>
            {[
              { label: 'Ingresos ARS Efectivo', valor: totalPor(ingresos, 'ARS', 'Efectivo'), color: theme.success, bg: theme.successBg },
              { label: 'Ingresos ARS Transferencia', valor: totalPor(ingresos, 'ARS', 'Transferencia'), color: theme.success, bg: theme.successBg },
              { label: 'Ingresos USD Efectivo', valor: totalPor(ingresos, 'USD', 'Efectivo'), color: theme.accent, bg: theme.infoBg, usd: true },
              { label: 'Ingresos USD Transferencia', valor: totalPor(ingresos, 'USD', 'Transferencia'), color: theme.accent, bg: theme.infoBg, usd: true },
              { label: 'Egresos ARS Efectivo', valor: totalPor(egresos, 'ARS', 'Efectivo'), color: theme.error, bg: theme.errorBg },
              { label: 'Egresos ARS Transferencia', valor: totalPor(egresos, 'ARS', 'Transferencia'), color: theme.error, bg: theme.errorBg },
              { label: 'Egresos USD Efectivo', valor: totalPor(egresos, 'USD', 'Efectivo'), color: theme.warning, bg: theme.warningBg, usd: true },
              { label: 'Egresos USD Transferencia', valor: totalPor(egresos, 'USD', 'Transferencia'), color: theme.warning, bg: theme.warningBg, usd: true },
            ].map(item => (
              <div key={item.label} style={{ background: item.bg, padding: '14px 16px', borderRadius: '10px' }}>
                <div style={{ fontSize: '11px', color: item.color, marginBottom: '6px', fontWeight: '500' }}>{item.label}</div>
                <div style={{ fontSize: '18px', fontWeight: '700', color: item.color }}>
                  {item.usd ? 'USD ' : '$'}{item.valor.toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                </div>
              </div>
            ))}
            <div style={{ gridColumn: '1 / -1', borderTop: `0.5px solid ${theme.border}`, paddingTop: '12px', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px' }}>
              {[
                { label: 'Disponible en Caja', sub: 'Efectivo ARS y USD', arsValor: totalPor(ingresos,'ARS','Efectivo') - totalPor(egresos,'ARS','Efectivo'), usdValor: totalPor(ingresos,'USD','Efectivo') - totalPor(egresos,'USD','Efectivo') },
                { label: 'Disponible en Banco', sub: 'Transferencias ARS y USD', arsValor: totalPor(ingresos,'ARS','Transferencia') - totalPor(egresos,'ARS','Transferencia'), usdValor: totalPor(ingresos,'USD','Transferencia') - totalPor(egresos,'USD','Transferencia') },
              ].map(item => (
                <div key={item.label} style={{ background: theme.accentSecondary, padding: '14px 16px', borderRadius: '10px' }}>
                  <div style={{ fontSize: '12px', color: theme.secondary, marginBottom: '2px', fontWeight: '600' }}>{item.label}</div>
                  <div style={{ fontSize: '11px', color: theme.secondary, marginBottom: '8px' }}>{item.sub}</div>
                  <div style={{ fontSize: '18px', fontWeight: '700', color: item.arsValor >= 0 ? theme.success : theme.error }}>
                    ${item.arsValor.toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                  </div>
                  <div style={{ fontSize: '13px', fontWeight: '600', color: item.usdValor >= 0 ? theme.success : theme.error, marginTop: '2px' }}>
                    USD {item.usdValor.toFixed(2)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {(!canVerTodo || tab === 'movimientos') && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {loading
              ? <div style={{ textAlign: 'center', color: theme.secondary, padding: '48px' }}>Cargando movimientos...</div>
              : movimientos.length === 0
                ? <div style={{ textAlign: 'center', color: theme.secondary, padding: '48px', fontSize: '13px' }}>No hay movimientos en este período</div>
                : movimientos.map(m => (
                    <div key={m.id} style={{ background: theme.bgCard, borderRadius: '10px', padding: '12px 16px', border: `0.5px solid ${theme.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                      <div>
                        <div style={{ fontSize: '13px', fontWeight: '500', color: theme.primary }}>
                          {m.categoria}{m.detalle ? ` · ${m.detalle}` : ''}
                          {m.editado && <span style={{ marginLeft: '6px', background: theme.warningBg, color: theme.warning, padding: '1px 6px', borderRadius: '99px', fontSize: '11px' }}>{m.cantidadEdiciones > 1 ? `⚠ Editado ${m.cantidadEdiciones}x` : 'Editado'}</span>}
                        </div>
                        <div style={{ fontSize: '11px', color: theme.secondary }}>{m.formaPago} · {m.paciente || '—'} · {m.registradoPor}</div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginLeft: 'auto', flexShrink: 0 }}>
                        {m.comprobante && (
                          <a href={m.comprobante.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '11px', padding: '3px 8px', borderRadius: '99px', background: theme.infoBg, color: theme.accent, textDecoration: 'none', fontWeight: '500' }}>
                            📎 {m.comprobante.nombre}
                          </a>
                        )}
                        {canEdit && !m.comprobante && (
                          <label style={{ fontSize: '11px', padding: '3px 8px', borderRadius: '99px', background: theme.bgCard, color: theme.secondary, cursor: 'pointer', fontWeight: '500', border: `0.5px solid ${theme.borderInput}` }}>
                            + Comprobante
                            <input type="file" accept=".jpg,.jpeg,.pdf,.png" style={{ display: 'none' }} onChange={async e => {
                              const archivo = e.target.files[0];
                              if (!archivo) return;
                              try {
                                const nombreLimpio = archivo.name.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9._-]/g, '');
                                const storageRef = ref(storage, `caja/${clinicaId}/${Date.now()}_${nombreLimpio}`);
                                await uploadBytes(storageRef, archivo);
                                const url = await getDownloadURL(storageRef);
                                await updateDoc(doc(db, 'caja', m.id), { comprobante: { nombre: archivo.name, url, tipo: archivo.type } });
                              } catch (err) { alert('Error: ' + err.message); }
                            }} />
                          </label>
                        )}
                        {canEdit && (
                          <button onClick={() => setModalEditar(m)} style={{ fontSize: '11px', padding: '3px 8px', borderRadius: '99px', background: theme.warningBg, color: theme.warning, border: 'none', cursor: 'pointer', fontFamily: "'Montserrat', sans-serif", fontWeight: '500' }}>
                            Editar
                          </button>
                        )}
                        <div style={{ fontSize: '16px', fontWeight: '700', color: m.tipo === 'ingreso' ? theme.success : theme.error }}>
                          {m.tipo === 'egreso' ? '−' : '+'}{formatMonto(m.monto, m.moneda)}
                        </div>
                      </div>
                    </div>
                  ))
            }
          </div>
        )}
      </div>

      {modalNuevo && (
        <div style={overlayStyle}>
          <div style={modalStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <span style={{ fontSize: '16px', fontWeight: '600', color: theme.primary }}>{tipoModal === 'ingreso' ? 'Registrar ingreso' : 'Registrar egreso'}</span>
              <button onClick={() => setModalNuevo(false)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: theme.secondary }}>×</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div><label style={labelStyle}>CATEGORÍA</label>
                <select value={nuevo.categoria} onChange={e => setNuevo({...nuevo, categoria: e.target.value})} style={inputStyle}>
                  {(tipoModal === 'ingreso' ? CATEGORIAS_INGRESO : CATEGORIAS_EGRESO).map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div><label style={labelStyle}>DETALLE</label><input value={nuevo.detalle} onChange={e => setNuevo({...nuevo, detalle: e.target.value})} style={inputStyle} /></div>
              <div><label style={labelStyle}>MONTO *</label><input type="number" value={nuevo.monto} onChange={e => setNuevo({...nuevo, monto: e.target.value})} style={inputStyle} /></div>
              <div><label style={labelStyle}>MONEDA</label>
                <select value={nuevo.moneda} onChange={e => setNuevo({...nuevo, moneda: e.target.value})} style={inputStyle}>
                  <option value="ARS">ARS — Pesos</option>
                  <option value="USD">USD — Dólares</option>
                </select>
              </div>
              <div><label style={labelStyle}>FORMA DE PAGO</label>
                <select value={nuevo.formaPago} onChange={e => setNuevo({...nuevo, formaPago: e.target.value})} style={inputStyle}>
                  <option value="Efectivo">Efectivo</option>
                  <option value="Transferencia">Transferencia</option>
                </select>
              </div>
              {tipoModal === 'ingreso' ? (
                <>
                  <div style={{ position: 'relative' }}>
                    <label style={labelStyle}>PACIENTE</label>
                    <input value={nuevo.paciente} onChange={e => { const v = e.target.value; setNuevo({...nuevo, paciente: v, pacienteId: ''}); if (v.length >= 2) setSugerenciasPacientes(pacientesDB.filter(p => `${p.apellido} ${p.nombre}`.toLowerCase().includes(v.toLowerCase()) || p.dni?.includes(v)).slice(0,5)); else setSugerenciasPacientes([]); }} placeholder="Apellido, nombre o DNI..." style={inputStyle} />
                    {sugerenciasPacientes.length > 0 && (
                      <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100, background: theme.bgModal, border: `0.5px solid ${theme.border}`, borderRadius: '8px', overflow: 'hidden', marginTop: '2px', boxShadow: '0 4px 16px rgba(0,0,0,0.3)' }}>
                        {sugerenciasPacientes.map(p => (
                          <div key={p.id} onClick={() => { setNuevo({...nuevo, paciente: `${p.apellido}, ${p.nombre}`, pacienteId: p.id, cobertura: p.cobertura || 'Particular'}); setSugerenciasPacientes([]); }} style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: `0.5px solid ${theme.border}` }} onMouseEnter={e => e.currentTarget.style.background = theme.bgCard} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                            <div style={{ fontSize: '13px', fontWeight: '500', color: theme.primary }}>{p.apellido}, {p.nombre}</div>
                            <div style={{ fontSize: '11px', color: theme.secondary }}>DNI {p.dni} · {p.cobertura}</div>
                          </div>
                        ))}
                        <div onClick={() => setSugerenciasPacientes([])} style={{ padding: '8px 14px', fontSize: '12px', color: theme.secondary, cursor: 'pointer', textAlign: 'center' }}>Continuar con nombre nuevo</div>
                      </div>
                    )}
                  </div>
                  <div style={{ gridColumn: '1 / -1' }}><label style={labelStyle}>COBERTURA</label>
                    <select value={nuevo.cobertura} onChange={e => setNuevo({...nuevo, cobertura: e.target.value})} style={inputStyle}>
                      <option value="Particular">Particular</option>
                      <option value="Obra Social / Prepaga">Obra Social / Prepaga</option>
                    </select>
                  </div>
                  <div style={{ gridColumn: '1 / -1' }}><label style={labelStyle}>MÉDICO ASOCIADO</label>
                    <select value={nuevo.medico} onChange={e => setNuevo({...nuevo, medico: e.target.value})} style={inputStyle}>
                      <option value="">Sin asignar</option>
                      <option value="medico1">{clinicaData?.medicos?.medico1 || 'Médico 1'}</option>
                      <option value="medico2">{clinicaData?.medicos?.medico2 || 'Médico 2'}</option>
                      <option value="medico3">{clinicaData?.medicos?.medico3 || 'Médico 3'}</option>
                      <option value="medico4">{clinicaData?.medicos?.medico4 || 'Médico 4'}</option>
                    </select>
                  </div>
                  <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input type="checkbox" checked={nuevo.esDerivacion || false} onChange={e => setNuevo({...nuevo, esDerivacion: e.target.checked, medicoDerivador: ''})} id="esDerivacion" />
                    <label htmlFor="esDerivacion" style={{ fontSize: '13px', color: theme.secondary, cursor: 'pointer' }}>Paciente derivado por médico externo</label>
                  </div>
                  {nuevo.esDerivacion && (
                    <div style={{ gridColumn: '1 / -1' }}>
                      <label style={labelStyle}>MÉDICO DERIVADOR</label>
                      <select value={nuevo.medicoDerivador || ''} onChange={e => setNuevo({...nuevo, medicoDerivador: e.target.value})} style={inputStyle}>
                        <option value="">Seleccionar...</option>
                        {derivadoresDB.map(d => <option key={d.id} value={d.nombre}>{d.nombre}{d.especialidad ? ` — ${d.especialidad}` : ''}</option>)}
                      </select>
                    </div>
                  )}

                </>
              ) : (
                <div style={{ gridColumn: '1 / -1' }}><label style={labelStyle}>PROVEEDOR / CONCEPTO</label><input value={nuevo.paciente} onChange={e => setNuevo({...nuevo, paciente: e.target.value})} style={inputStyle} /></div>
              )}
              <div style={{ gridColumn: '1 / -1' }}><label style={labelStyle}>OBSERVACIONES</label><textarea value={nuevo.observaciones} onChange={e => setNuevo({...nuevo, observaciones: e.target.value})} rows={2} style={{ ...inputStyle, resize: 'vertical' }} /></div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={labelStyle}>COMPROBANTE / FACTURA (opcional)</label>
                <input type="file" accept=".jpg,.jpeg,.pdf,.png" onChange={e => setArchivoMovimiento(e.target.files[0] || null)} style={{ fontSize: '13px', color: theme.secondary, fontFamily: "'Montserrat', sans-serif" }} />
                {archivoMovimiento && <span style={{ fontSize: '11px', marginTop: '4px', display: 'inline-block', color: theme.success, background: theme.successBg, padding: '3px 8px', borderRadius: '99px' }}>{archivoMovimiento.name}</span>}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px', marginTop: '20px' }}>
              <button onClick={registrarMovimiento} style={btnPrimaryStyle}>Guardar</button>
              <button onClick={() => setModalNuevo(false)} style={btnSecondaryStyle}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {modalEditar && (
        <div style={overlayStyle}>
          <div style={modalStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <span style={{ fontSize: '16px', fontWeight: '600', color: theme.primary }}>Editar movimiento</span>
              <button onClick={() => { setModalEditar(null); setDatosEditar({}); }} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: theme.secondary }}>×</button>
            </div>
            <div style={{ background: theme.warningBg, padding: '10px 12px', borderRadius: '8px', marginBottom: '16px', fontSize: '12px', color: theme.warning }}>
              ⚠ Cada edición queda registrada en la trazabilidad.
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div><label style={labelStyle}>MONTO</label><input type="number" defaultValue={modalEditar.monto} onChange={e => setDatosEditar({...datosEditar, monto: parseFloat(e.target.value)})} style={inputStyle} /></div>
              <div><label style={labelStyle}>CATEGORÍA</label>
                <select defaultValue={modalEditar.categoria} onChange={e => setDatosEditar({...datosEditar, categoria: e.target.value})} style={inputStyle}>
                  {(modalEditar.tipo === 'ingreso' ? CATEGORIAS_INGRESO : CATEGORIAS_EGRESO).map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div><label style={labelStyle}>DETALLE</label><input defaultValue={modalEditar.detalle} onChange={e => setDatosEditar({...datosEditar, detalle: e.target.value})} style={inputStyle} /></div>
              <div><label style={labelStyle}>OBSERVACIONES</label><textarea defaultValue={modalEditar.observaciones} onChange={e => setDatosEditar({...datosEditar, observaciones: e.target.value})} rows={2} style={{ ...inputStyle, resize: 'vertical' }} /></div>
            </div>
            <div style={{ display: 'flex', gap: '8px', marginTop: '20px' }}>
              <button onClick={async () => {
                const cantidadActual = modalEditar.cantidadEdiciones || 1;
                await updateDoc(doc(db, 'caja', modalEditar.id), { ...datosEditar, editado: true, editadoPor: currentUser?.email, fechaEdicion: new Date(), cantidadEdiciones: cantidadActual + 1 });
                await registrarAccion(currentUser?.email, userRole, 'Caja', cantidadActual >= 1 ? '⚠ Edición múltiple' : 'Editar movimiento', `ID: ${modalEditar.id} · Edición #${cantidadActual + 1}`, clinicaId);
                setModalEditar(null); setDatosEditar({});
              }} style={btnPrimaryStyle}>Confirmar edición</button>
              <button onClick={() => { setModalEditar(null); setDatosEditar({}); }} style={btnSecondaryStyle}>Cancelar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}